<script lang="ts">
  /**
   * Unified per-entity activity log. Date on the left, no connecting line, a colored
   * status ring, status badges, and a provenance "source" line (icon · label · time).
   * Filter chips toggle which kinds are shown. Works for any entity.
   */
  import { onMount } from 'svelte';
  import Icon from './Icon.svelte';
  import type { IconName } from '../lib/icons';
  import { fmtDate, fmtDateTime } from '../lib/format';
  import { fetchEntityTimeline, type TimelineItem } from '../lib/timeline-api';

  let { entityType, entityId }: { entityType: string; entityId: string } = $props();

  let items = $state<TimelineItem[]>([]);
  let loading = $state(true);
  let err = $state<string | null>(null);
  let active = $state<Set<TimelineItem['kind']>>(new Set()); // empty = all

  async function load() {
    if (!entityType || !entityId) { loading = false; return; }
    loading = true;
    try {
      const r = await fetchEntityTimeline(entityType, entityId);
      items = r.items;
      err = null;
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  }
  $effect(() => { void entityType; void entityId; void load(); });

  const KIND_META: Record<TimelineItem['kind'], { label: string; color: string }> = {
    payment: { label: 'Оплати', color: 'var(--color-pu-3)' },
    event: { label: 'Події', color: 'var(--color-pu-2)' },
    change: { label: 'Зміни', color: '#d97706' },
    automation_run: { label: 'Автоматизації', color: '#F59E0B' },
    relation: { label: 'Зв’язки', color: '#0891b2' },
    funnel_transition: { label: 'Воронки', color: '#16a34a' },
  };
  const ORDER: TimelineItem['kind'][] = ['payment', 'event', 'change', 'automation_run', 'relation', 'funnel_transition'];

  const presentKinds = $derived(ORDER.filter((k) => items.some((i) => i.kind === k)));
  const filtered = $derived(active.size === 0 ? items : items.filter((i) => active.has(i.kind)));

  function toggle(k: TimelineItem['kind']) {
    const next = new Set(active);
    next.has(k) ? next.delete(k) : next.add(k);
    active = next;
  }

  function dotColor(it: TimelineItem): string {
    const s = (it.status ?? '').toString().toUpperCase();
    if (it.kind === 'payment') {
      if (s === 'REFUNDED') return '#DC2626';
      if (s === 'FAILED') return '#9CA3AF';
      return KIND_META.payment.color;
    }
    if (it.kind === 'automation_run') {
      if (s === 'FAILED') return '#DC2626';
      if (s === 'SKIPPED') return '#9CA3AF';
      return KIND_META.automation_run.color;
    }
    return KIND_META[it.kind].color;
  }
  function showBadge(it: TimelineItem): boolean {
    if (!it.status) return false;
    const s = String(it.status).toUpperCase();
    return s !== 'SUCCEEDED' && s !== 'SUCCESS';
  }
  function iconFor(it: TimelineItem): IconName {
    return (it.icon as IconName) ?? 'dot';
  }

  interface ChangeDelta { field: string; label: string; old: unknown; new: unknown }
  function deltasOf(it: TimelineItem): ChangeDelta[] {
    const d = (it.meta as { deltas?: ChangeDelta[] } | undefined)?.deltas;
    return Array.isArray(d) ? d : [];
  }
  function fmtVal(v: unknown): string {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'boolean') return v ? 'так' : 'ні';
    if (Array.isArray(v)) return v.length ? v.map(fmtVal).join(', ') : '—';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  }
</script>

<div class="rounded-xl border border-[var(--color-border-3)] bg-white p-4">
  <div class="flex items-center justify-between gap-3 mb-3 flex-wrap">
    <div class="text-[13px] font-bold">Активність {#if items.length}<span class="text-[var(--color-text-muted)] ml-1 text-xs">{filtered.length}</span>{/if}</div>
    {#if presentKinds.length > 1}
      <div class="flex items-center gap-1 flex-wrap">
        <button
          onclick={() => (active = new Set())}
          class="rounded-full px-2.5 py-1 text-[10.5px] font-semibold border transition-colors
            {active.size === 0 ? 'border-[var(--color-pu)] bg-purple-50 text-[var(--color-pu-3)]' : 'border-[var(--color-border-3)] text-[var(--color-text-muted)] hover:bg-[var(--color-bg-3)]'}"
        >Всі</button>
        {#each presentKinds as k (k)}
          <button
            onclick={() => toggle(k)}
            class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-semibold border transition-colors
              {active.has(k) ? 'border-[var(--color-pu)] bg-purple-50 text-[var(--color-pu-3)]' : 'border-[var(--color-border-3)] text-[var(--color-text-muted)] hover:bg-[var(--color-bg-3)]'}"
          >
            <span class="h-1.5 w-1.5 rounded-full" style="background: {KIND_META[k].color};"></span>
            {KIND_META[k].label}
          </button>
        {/each}
      </div>
    {/if}
  </div>

  {#if loading}
    <div class="text-xs text-[var(--color-text-muted)]">Завантаження…</div>
  {:else if err}
    <div class="text-[11px] text-red-600">{err}</div>
  {:else if filtered.length === 0}
    <div class="text-[11px] italic text-[var(--color-text-muted-2)]">Поки немає активності.</div>
  {:else}
    <div class="flex flex-col">
      {#each filtered as it (it.id)}
        <div class="grid grid-cols-[88px_16px_1fr] gap-2.5 items-start pb-3.5 last:pb-0">
          <!-- date -->
          <div class="text-[11.5px] text-[var(--color-text-muted-2)] tabular-nums whitespace-nowrap pt-0.5">{fmtDate(it.occurredAt)}</div>
          <!-- ring (no connecting line) -->
          <div class="flex justify-center pt-1">
            <div class="h-3.5 w-3.5 rounded-full bg-white border-2" style="border-color: {dotColor(it)};"></div>
          </div>
          <!-- content -->
          <div class="min-w-0">
            <div class="flex items-center gap-1.5 flex-wrap">
              <span class="inline-flex text-[var(--color-text-muted)]"><Icon name={iconFor(it)} size={14} /></span>
              <span class="text-[13px] font-bold {String(it.status).toUpperCase() === 'REFUNDED' ? 'text-red-600' : ''}">{it.title}</span>
              {#if showBadge(it)}
                <span class="inline-block rounded-full bg-[var(--color-bg-2)] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[var(--color-text-muted)]">{it.status}</span>
              {/if}
            </div>

            {#if it.subtitle}
              <div class="text-[10.5px] text-[var(--color-text-muted)] mt-0.5">{it.subtitle}</div>
            {/if}

            {#if it.kind === 'change'}
              {#each deltasOf(it) as d (d.field)}
                <div class="mt-0.5 flex items-center gap-1.5 text-[10.5px] min-w-0">
                  {#if deltasOf(it).length > 1}<span class="text-[var(--color-text-muted)] shrink-0">{d.label}:</span>{/if}
                  <span class="text-[var(--color-text-muted-2)] line-through truncate max-w-[45%]">{fmtVal(d.old)}</span>
                  <span class="text-[var(--color-text-muted)] shrink-0">→</span>
                  <span class="font-medium truncate max-w-[45%]">{fmtVal(d.new)}</span>
                </div>
              {/each}
            {/if}

            {#if it.source}
              <div class="mt-0.5 flex items-center gap-1.5 text-[10.5px] text-[var(--color-text-muted)] min-w-0">
                {#if it.source.icon}<span class="inline-flex shrink-0"><Icon name={it.source.icon as IconName} size={12} /></span>{/if}
                {#if it.source.link}
                  <a href={it.source.link} class="text-[var(--color-pu-3)] no-underline hover:underline truncate">{it.source.label}</a>
                {:else}
                  <span class="truncate">{it.source.label}</span>
                {/if}
                {#if it.source.detail}<span class="text-[var(--color-text-muted-2)] truncate">· {it.source.detail}</span>{/if}
                {#if it.source.at}<span class="text-[var(--color-text-muted-2)] whitespace-nowrap shrink-0">· {fmtDateTime(it.source.at)}</span>{/if}
              </div>
            {/if}
          </div>
        </div>
      {/each}
    </div>
  {/if}
</div>
